"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import type { SaleHistory } from "@/schemas/analyzed-property";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BanknoteIcon,
  Building2,
  ChevronDown,
  DollarSign,
  FileText,
  HandCoins,
  User2,
} from "lucide-react";
import { useState } from "react";

interface OwnershipTimelineProps {
  saleHistory?: SaleHistory[];
  className?: string;
}

function TransactionIcon({
  type,
  className,
}: { type: string; className?: string }) {
  if (type.includes("Foreclosure")) return <Building2 className={className} />;
  if (type.includes("Cash Purchase"))
    return <HandCoins className={className} />;
  if (type.includes("Financed")) return <BanknoteIcon className={className} />;
  if (type.includes("Intrafamily")) return <User2 className={className} />;
  return <ArrowRight className={className} />;
}

export function OwnershipTimeline({
  saleHistory,
  className,
}: OwnershipTimelineProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Early return if no sale history
  if (!saleHistory || saleHistory.length === 0) return null;

  // Sort entries by date (newest first)
  const sortedEntries = [...saleHistory].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  // Display only the first two entries by default
  const displayEntries = isExpanded ? sortedEntries : sortedEntries.slice(0, 1);
  const hasMoreEntries = sortedEntries.length > 1;

  return (
    <div className={className}>
      <div className="flex flex-col gap-1.5 mb-4">
        <h3 className="text-lg font-semibold">Ownership History</h3>
        <p className="text-sm text-muted-foreground">
          Previous ownership changes and transaction details
        </p>
      </div>

      <div className="relative space-y-4">
        {/* Timeline track - appears when expanded or has more than one entry */}
        {displayEntries.length > 1 && (
          <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-amber-200 via-amber-200/70 to-amber-200/30 z-0" />
        )}

        {/* Timeline entries */}
        <AnimatePresence initial={false}>
          {displayEntries.map((entry, index) => (
            <motion.div
              key={`${entry.date}-${entry.buyer}-${entry.amount}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="relative z-10"
            >
              <Card className="flex flex-col sm:flex-row overflow-hidden border border-gray-200 transition-all hover:border-amber-200/50 hover:shadow-md">
                {/* Top date banner for mobile */}
                <div className="relative flex flex-row sm:flex-col items-center justify-start p-3 sm:p-4 bg-gradient-to-r sm:bg-gradient-to-b from-amber-50/80 to-amber-50/30 border-b sm:border-b-0 sm:border-r border-gray-100">
                  <div className="z-10 flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 ring-4 ring-white">
                    <TransactionIcon
                      type={entry.transaction_type}
                      className="h-4 w-4 text-amber-600"
                    />
                  </div>
                  <div className="ml-3 sm:ml-0 sm:mt-2 text-center">
                    <div className="text-xs font-semibold text-amber-700">
                      {format(new Date(entry.date), "MMM")}
                    </div>
                    <div className="text-lg font-bold text-amber-900">
                      {format(new Date(entry.date), "yyyy")}
                    </div>
                  </div>
                </div>

                {/* Main content */}
                <div className="flex-1 flex flex-col divide-y divide-gray-100 sm:divide-y-0">
                  {/* Price section */}
                  <div className="p-3 sm:p-4 sm:pb-2">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold">
                          {formatCurrency(entry.amount)}
                        </span>
                        {entry.purchase_method && (
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                            {entry.purchase_method}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(entry.date), "MMMM d, yyyy")}
                      </span>
                    </div>
                  </div>

                  {/* Transaction participants section */}
                  <div className="p-3 sm:p-4 sm:py-2 bg-gray-50/50 sm:bg-transparent">
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-12 font-semibold text-gray-700">
                          From:
                        </span>
                        <span className="text-gray-600 flex-1">
                          {entry.seller}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-12 font-semibold text-gray-700">
                          To:
                        </span>
                        <span className="text-gray-600 flex-1">
                          {entry.buyer}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Document info section */}
                  <div className="p-3 sm:p-4 sm:pt-2">
                    <div className="flex flex-col sm:flex-row gap-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5" />
                        <span>{entry.document_type}</span>
                      </div>
                      <div className="hidden sm:block text-gray-300">•</div>
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="h-3.5 w-3.5" />
                        <span>
                          {entry.transaction_type.replace(
                            "Arms Length Residential Transactions",
                            "Standard Sale",
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Show more/less button */}
        {hasMoreEntries && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex justify-center pt-2"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full border border-gray-200 hover:bg-gray-50"
            >
              <div className="flex items-center gap-1">
                {isExpanded ? (
                  <>
                    <ChevronDown className="h-4 w-4 rotate-180" />
                    <span>Show Less</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span>
                      Show {sortedEntries.length - 1} More Transaction
                      {sortedEntries.length - 1 !== 1 ? "s" : ""}
                    </span>
                  </>
                )}
              </div>
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
